/**
 * Pluckonomics — Cohort matrix (Phase E.3).
 *
 * Reads `GET /api/admin/analytics/cohort?months=N`.
 * Rows: signup-month cohorts (oldest first).
 * Columns: months-since-signup offsets (M0, M1, ...).
 * Cells: % of cohort who placed ≥1 booking by end of that offset month.
 * Future offsets render "—" (cohort window hasn't fully elapsed yet).
 *
 * Heatmap shading: cell color intensity scales with conversion %
 * (0% = surface, 100% = darker accent). Empty cohorts render a single
 * "—" across the row with size=0.
 */
import { Card, CardContent } from "@/components/ui/card";

function _heatmapStyle(pct) {
  if (pct === null || pct === undefined) {
    return { background: "transparent", color: "var(--pp-text-soft)" };
  }
  // Map 0..100 → opacity 0.05..0.5 against the purple v3 accent.
  const a = Math.max(0.05, Math.min(0.55, pct / 100 * 0.55));
  return {
    background: `rgba(87, 75, 140, ${a.toFixed(2)})`,
    color: pct > 50 ? "#fff" : "var(--pp-text)",
  };
}

function _formatPct(p) {
  if (p === null || p === undefined) return "—";
  return `${p.toFixed(1)}%`;
}

export default function CohortMatrix({ data }) {
  if (!data) {
    return (
      <div className="p-6 text-sm" style={{ color: "var(--pp-text-soft)" }}
           data-testid="pluckonomics-cohort-loading">
        Loading cohort matrix…
      </div>
    );
  }
  const { cohorts, offset_labels } = data;
  const totalCohortSize = cohorts.reduce((acc, c) => acc + (c.size || 0), 0);
  return (
    <Card
      style={{ background: "var(--pp-v3-surface)", borderColor: "var(--pp-border)" }}
      data-testid="pluckonomics-cohort-card"
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: "var(--pp-text)" }}
                data-testid="pluckonomics-cohort-heading">
              Customer cohort matrix
            </h2>
            <p className="mt-1 text-xs" style={{ color: "var(--pp-text-soft)" }}>
              % of each signup cohort that placed their first booking by end of
              offset month. Future offsets show &quot;—&quot; until the window closes.
            </p>
          </div>
          <div className="text-right text-xs" style={{ color: "var(--pp-text-soft)" }}>
            <div>Cohorts: {cohorts.length}</div>
            <div>Total customers: {totalCohortSize.toLocaleString("en-US")}</div>
          </div>
        </div>

        <div className="overflow-x-auto" data-testid="pluckonomics-cohort-table-wrap">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="py-2 pr-3 text-left text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)", borderBottom: "1px solid var(--pp-border)" }}>
                  Cohort
                </th>
                <th className="py-2 px-3 text-right text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)", borderBottom: "1px solid var(--pp-border)" }}>
                  Size
                </th>
                {offset_labels.map((lbl, idx) => (
                  <th key={lbl}
                      className="py-2 px-3 text-center text-[11px] font-bold uppercase tracking-[0.06em]"
                      style={{ color: "var(--pp-text-soft)", borderBottom: "1px solid var(--pp-border)" }}
                      data-testid={`pluckonomics-cohort-col-${lbl}`}>
                    {lbl}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohorts.map((cohort, idx) => (
                <tr key={cohort.cohort_month}
                    data-testid={`pluckonomics-cohort-row-${cohort.cohort_month}`}
                    style={{ borderBottom: "1px solid var(--pp-border)" }}>
                  <td className="py-2 pr-3 font-medium" style={{ color: "var(--pp-text)" }}>
                    {cohort.cohort_label}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums"
                      style={{ color: "var(--pp-text-soft)" }}>
                    {cohort.size > 0 ? cohort.size.toLocaleString("en-US") : "0"}
                  </td>
                  {cohort.conversion_pct_by_offset.map((pct, i) => (
                    <td key={i}
                        className="py-2 px-3 text-center tabular-nums"
                        style={{
                          ..._heatmapStyle(cohort.size === 0 ? null : pct),
                          fontWeight: 600,
                          minWidth: 64,
                        }}
                        data-testid={`pluckonomics-cohort-cell-${cohort.cohort_month}-${offset_labels[i]}`}>
                      {cohort.size === 0 ? "—" : _formatPct(pct)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center gap-3 text-[11px]"
             style={{ color: "var(--pp-text-soft)" }}>
          <span>Conversion intensity:</span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded"
                  style={_heatmapStyle(10)} />
            <span>low</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded"
                  style={_heatmapStyle(50)} />
            <span>mid</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded"
                  style={_heatmapStyle(95)} />
            <span>high</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
